import { useState } from "react"

import { 
  CustomModal, 
  ErrorPlaceholder, 
  LoadingPlaceholder, 
  IconList,
  ReadOnlyBadge,
  SuccessPlaceholder,
} from "@components"

import { DELETE_BOOKING_SLOT_MUTATION } from "@graphql"

import { hasExpired, mySqlToLocal } from "@helpers"
import { useMutation } from "@apollo/client"

import { 
  Button,
  Chip,
  ModalContent, 
  ModalFooter,
  ModalHeader, 
  ModalBody, 
} from "@heroui/react"

import { 
  AcademicCapIcon,
  BookmarkSquareIcon, 
  BookOpenIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon, 
  CalendarIcon,
  CircleStackIcon, 
  InformationCircleIcon,
  PhoneArrowDownLeftIcon,
  UserGroupIcon,
  UserIcon, 
  UsersIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline"

import PropTypes from "prop-types"

const statusConfig = {
  AVAILABLE: "bg-timetable-available",
  BOOKED:    "bg-timetable-booked",
  DISABLED:  "bg-timetable-highlight-disabled",
  ABSENT:    "bg-timetable-absent",
  ATTENDED:  "bg-timetable-attended",
}

const meetingTypeIcons = {
  "VENUE": <BuildingOffice2Icon />,
  "PHONE": <PhoneArrowDownLeftIcon />,
  "VC": <VideoCameraIcon />,
}

const SlotDetailsModal = ({ 
  details, 
  error, 
  handleClose, 
  loading,
  session,
  updateTimetableDeletedBookingCache,
}) => {  
  const hasSessionExpired = hasExpired(mySqlToLocal(session.ends_at))
  const [showSuccess, setShowSuccess] = useState(false)
  const [deleteBookingSlot, { loading: isDeleting }] = useMutation(DELETE_BOOKING_SLOT_MUTATION, {
    onCompleted: () => setShowSuccess(true),
    update: (cache) => updateTimetableDeletedBookingCache(cache, details.booking_id, details.slot_id)
  })

  const handleDeleteBookingSlots = () => {
    deleteBookingSlot({ 
      variables: {
        input: {
          booking_id: details.booking_id, 
          slot_id: details.slot_id,
        }
      }
    })
  }

  const meetingTypeIcon = meetingTypeIcons[details?.meeting_type_code] || <InformationCircleIcon />
  const statusClasses = statusConfig[details?.status] || "bg-indigo-500"
  const showFooter = !loading && !error && details && !showSuccess && !hasSessionExpired
  
  const getModalBody = () => {
    if (showSuccess) {
      return <SuccessPlaceholder classNames="my-4" title="Booking Removed" message="Booking removed successfully." />
    }

    if (error) {
      return <ErrorPlaceholder classNames="my-4" caption="There was an error loading the slot data." error={error} />
    }

    if (loading || !details) {
      return <LoadingPlaceholder classNames="my-4" caption="Please wait. Loading slot data..." icon={CircleStackIcon} />
    }

    return (
      <div>
        <div className="flex justify-between gap-12 px-4 pt-4 mb-4">
          <IconList
            textColor="text-white/80"
            items={[
              { icon: <CalendarDaysIcon />, value: details.starts_at ? mySqlToLocal(details.starts_at).format("H:mm") : "N/A", caption: "Start Time" },
              { icon: <UserGroupIcon />, value: details.group || "N/A", caption: "Teacher" },
              { icon: <BookOpenIcon />, value: details.subject || "N/A", caption: "Subject" },
              { icon: <AcademicCapIcon />, value: details.student || "N/A", caption: "Student" },
              { icon: <UserIcon />, value: details.booked_by || "N/A", caption: "Booked By" },
            ]}
          />
          
          <IconList
            textColor="text-white/80"
            items={[
              { icon: <CalendarDaysIcon />, value: details.ends_at ? mySqlToLocal(details.ends_at).format("H:mm") : "N/A", caption: "End Time" },
              { icon: <BookmarkSquareIcon />, value: details.lesson || "N/A", caption: "Lesson" },
              { icon: meetingTypeIcon, value: details.meeting_type || "N/A", caption: "Meeting Type" },
              { icon: <UsersIcon />, value: details.guardian || "N/A", caption: "Guardian" },
              { icon: <CalendarIcon />, value: details.date_booked ? mySqlToLocal(details.date_booked).format("DD/MM/YYYY H:mm") : "N/A", caption: "Date Booked" },
            ]}
          />
        </div>
      </div>
    )
  }

  const modalBody = getModalBody();

  return (
    <CustomModal 
      isOpen={true}
      classNames={{ 
        body: "gap-0",
        header: "bg-indigo-800 rounded-t-xl text-white",
        wrapper: "md:items-center",
        closeButton: "hover:bg-white/5 text-white mt-2 mr-2",
      }}         
      size="2xl"
      onClose={handleClose}
      isDismissable={true}
      isKeyboardDismissDisabled={false}
      scrollBehavior="inside"  
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-row justify-between gap-1">
              Slot Details

              <div className="flex items-center gap-3">
                {!loading && !error && !showSuccess && details && hasSessionExpired && <ReadOnlyBadge size="lg" />}

                {!loading && !error && !showSuccess && details && (
                  <Chip size="lg" classNames={{ content: `font-semibold text-white`, base: `mr-8 ${statusClasses}` }}>
                    {details.status || "Unknown Status"}
                  </Chip>
                )}
              </div>
            </ModalHeader>

            <ModalBody>
              <div>
                {modalBody}
              </div>
            </ModalBody>
            
            {showFooter && (
              <ModalFooter className="flex justify-between pt-0">
                <Button color="default" onClick={handleClose}>
                  Close
                </Button>

                <Button color="danger" className="font-semibold" onClick={handleDeleteBookingSlots} isLoading={isDeleting}>
                  Remove Booking
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}


SlotDetailsModal.propTypes = {
  details: PropTypes.shape({
    status: PropTypes.string,
    starts_at: PropTypes.string,
    ends_at: PropTypes.string,
    group: PropTypes.string,
    subject: PropTypes.string,
    student: PropTypes.string,
    booked_by: PropTypes.string,
    lesson: PropTypes.string,
    meeting_type: PropTypes.string,
    guardian: PropTypes.string,
    date_booked: PropTypes.string,
  }),
  error: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  updateTimetableDeletedBookingCache: PropTypes.func.isRequired,
}

export default SlotDetailsModal
